import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { buildSemanticQuery } from "@/lib/widgetCatalog";
import { effectiveWhatIfParams } from "@/lib/canvasUtils";
import { formatValue } from "@/lib/chartTheme";
import { Loader2, Inbox, FlaskConical, TrendingUp, TrendingDown } from "lucide-react";

// Scenario Compare widget: shows the configured measures side-by-side for
// Baseline + every named scenario saved on the dashboard (canvas.scenarios).
export function ScenarioGridWidget({ widget, publicToken = null, globalFilters = [], whatIfDefs = [], scenarios = [], currentParams = null }) {
  const config = widget.config || {};
  const measures = (config.measures || []).filter((m) => m && (m.key || m.field));
  const columns = [{ id: "__baseline", name: "Baseline", values: {} }, ...scenarios];
  const [data, setData] = useState(null);   // { [colId]: {row, meta} }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchKey = JSON.stringify({ c: config, f: globalFilters, d: whatIfDefs, s: scenarios, p: publicToken });
  useEffect(() => {
    let active = true;
    if (!widget.dataset_id || !measures.length || !whatIfDefs.length) { setData(null); return; }
    setLoading(true); setError(null);
    const run = async (col) => {
      const sq = buildSemanticQuery({ ...config, dimensions: [] }, globalFilters);
      const params = effectiveWhatIfParams(whatIfDefs, col.values || {});
      if (params) sq.params = params;
      const { data: d } = publicToken
        ? await api.post("/public/model/query", { token: publicToken, dataset_id: widget.dataset_id, semantic_query: sq }, { timeout: 45000 })
        : await api.post(`/datasets/${widget.dataset_id}/model/query`, sq, { timeout: 45000 });
      return [col.id || col.name, { row: (d.rows || [])[0] || {}, meta: d.meta || {} }];
    };
    Promise.all(columns.map(run))
      .then((entries) => { if (active) setData(Object.fromEntries(entries)); })
      .catch((e) => { if (active) setError(e?.response?.data?.detail || "Failed to load scenario data"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // keyed by serialized config/filters/defs/scenarios
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey, widget.dataset_id]);

  if (!widget.dataset_id || !measures.length) {
    return <CenterMsg icon={Inbox} title="Add a measure" text="Pick a dataset and at least one measure — each saved scenario becomes a column." />;
  }
  if (!whatIfDefs.length) {
    return <CenterMsg icon={FlaskConical} title="No what-if parameters" text="Define what-if parameters (What-if bar → Manage), then save scenarios to compare them here." />;
  }
  if (loading && !data) return <div className="h-full w-full flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[color:var(--brand-ocean)]" /></div>;
  if (error) return <CenterMsg icon={Inbox} title="Query failed" text={error} danger />;
  if (!data) return null;

  const meta0 = data.__baseline?.meta?.measures || [];
  const keyOf = (m) => m.key || m.alias || `${m.agg || "sum"}_${m.field}`;
  const labelOf = (m) => m.label || meta0.find((x) => x.key === keyOf(m))?.label || keyOf(m).replace(/_/g, " ");
  const fmtOf = (m) => m.format || meta0.find((x) => x.key === keyOf(m))?.format;
  const curKey = JSON.stringify(currentParams || {});
  const isActiveCol = (col) => JSON.stringify(effectiveWhatIfParams(whatIfDefs, col.values || {}) || {}) === curKey;

  return (
    <div data-testid="scenario-grid" className="h-full w-full overflow-auto px-1">
      <table className="w-full text-sm border-separate" style={{ borderSpacing: 0 }}>
        <thead>
          <tr>
            <th className="text-left text-[11px] font-medium text-[color:var(--ink-3)] py-1.5 pr-2 sticky left-0 bg-[color:var(--surface)]">Measure</th>
            {columns.map((col) => (
              <th key={col.id || col.name} data-testid="scenario-grid-col"
                className={"text-right text-[11px] font-semibold py-1.5 px-2 whitespace-nowrap " +
                  (isActiveCol(col) ? "text-[color:var(--brand-mint)]" : "text-[color:var(--ink-2)]")}>
                {col.name}{isActiveCol(col) && <span className="ml-1 text-[9px] px-1 py-0.5 rounded-full bg-[color:rgba(0,194,168,0.14)]">active</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {measures.map((m) => {
            const k = keyOf(m);
            const baseV = Number(data.__baseline?.row?.[k]);
            return (
              <tr key={k} className="border-t border-[color:var(--glass-border)]">
                <td className="text-xs text-[color:var(--ink-2)] py-2 pr-2 font-medium sticky left-0 bg-[color:var(--surface)]">{labelOf(m)}</td>
                {columns.map((col, ci) => {
                  const v = Number(data[col.id || col.name]?.row?.[k]);
                  const has = Number.isFinite(v);
                  const delta = ci > 0 && has && Number.isFinite(baseV) && baseV !== 0
                    ? ((v - baseV) / Math.abs(baseV)) * 100 : null;
                  return (
                    <td key={col.id || col.name} data-testid="scenario-grid-cell" className="text-right py-2 px-2 align-top">
                      <div className="tabular-nums font-semibold text-[color:var(--ink)]">{has ? formatValue(v, fmtOf(m)) : "—"}</div>
                      {delta != null && Math.abs(delta) >= 0.05 && (
                        <div data-testid="scenario-grid-delta"
                          className={"inline-flex items-center gap-0.5 text-[10px] font-semibold mt-0.5 " +
                            (delta >= 0 ? "text-[color:var(--success)]" : "text-[color:var(--danger)]")}>
                          {delta >= 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                          {delta >= 0 ? "+" : ""}{delta.toFixed(1)}%
                        </div>
                      )}
                      {ci === 0 && <div className="text-[10px] text-[color:var(--ink-3)] mt-0.5">baseline</div>}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {scenarios.length === 0 && (
        <div className="mt-2 text-[11px] text-[color:var(--ink-3)] px-1">No saved scenarios yet — save some from the What-if bar to compare them here.</div>
      )}
      {loading && <div className="absolute top-1 left-1"><Loader2 className="w-3.5 h-3.5 animate-spin text-[color:var(--brand-ocean)]" /></div>}
    </div>
  );
}

function CenterMsg({ icon: Icon, title, text, danger = false }) {
  return (
    <div className="h-full w-full flex items-center justify-center">
      <div className={"text-center text-sm px-4 " + (danger ? "text-[color:var(--danger)]" : "text-[color:var(--ink-3)]")}>
        <Icon className="w-6 h-6 mx-auto mb-2 opacity-60" />
        <div className="font-medium text-[color:var(--ink-2)]">{title}</div>
        <div className="text-xs mt-1">{text}</div>
      </div>
    </div>
  );
}
